// Client-side, zero-knowledge encryption for the share feature.
// The diagram is encrypted IN THE BROWSER with a key derived from a user-chosen
// password (PBKDF2-HMAC-SHA256) and AES-256-GCM. Only the ciphertext and the
// non-secret KDF parameters (iv, salt, iterations) ever reach the server, so the
// operator can never read the content. The password is shared out-of-band by the
// editor and is never transmitted.

const KDF = 'PBKDF2-SHA256';
const ALGO = 'AES-GCM';
// OWASP-recommended PBKDF2-HMAC-SHA256 work factor (2023+).
const ITERATIONS = 210000;
const VERSION = 1;

function subtle() {
  const c = typeof window !== 'undefined' ? window.crypto : globalThis.crypto;
  if (!c || !c.subtle) return null;
  return c;
}

// Web Crypto requires a secure context (https/localhost). Surface this clearly.
export function isCryptoAvailable() {
  return !!subtle();
}

function bufToB64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function deriveKey(crypto, password, salt, iterations, usages) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    keyMaterial,
    { name: ALGO, length: 256 },
    false,
    usages
  );
}

// Encrypt a JSON-serialisable object -> opaque payload (all base64 strings).
export async function encryptJSON(obj, password) {
  const crypto = subtle();
  if (!crypto) throw new Error('crypto-unavailable');
  const enc = new TextEncoder();
  const data = enc.encode(JSON.stringify(obj));
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(crypto, password, salt, ITERATIONS, ['encrypt']);
  const ct = await crypto.subtle.encrypt({ name: ALGO, iv }, key, data);
  return {
    ciphertext: bufToB64(ct),
    iv: bufToB64(iv),
    salt: bufToB64(salt),
    iterations: ITERATIONS,
    algo: ALGO,
    kdf: KDF,
    version: VERSION,
  };
}

// Decrypt an opaque payload back to the original object. Throws on a wrong
// password (AES-GCM authentication failure) or tampered data.
export async function decryptJSON(payload, password) {
  const crypto = subtle();
  if (!crypto) throw new Error('crypto-unavailable');
  const iterations = payload.iterations || ITERATIONS;
  const salt = b64ToBytes(payload.salt);
  const iv = b64ToBytes(payload.iv);
  const ct = b64ToBytes(payload.ciphertext);
  const key = await deriveKey(crypto, password, salt, iterations, ['decrypt']);
  const plain = await crypto.subtle.decrypt({ name: ALGO, iv }, key, ct);
  const dec = new TextDecoder();
  return JSON.parse(dec.decode(plain));
}
